<template>
  <div class="api-keys-list">
    <b-form-row class="mb-3">
      <b-form-group class="col-lg-4" label="Search">
        <b-form-input
          v-model="filters.search"
          size="sm"
          placeholder="Search by name..."
          @input="debouncedSearch"
        />
      </b-form-group>

      <b-form-group class="col-lg-3" label="Environment">
        <b-form-select
          v-model="filters.environment"
          :options="environmentFilterOptions"
          size="sm"
          @change="loadApiKeys"
        />
      </b-form-group>

      <b-form-group class="col-lg-3" label="Status">
        <b-form-select
          v-model="filters.status"
          :options="statusFilterOptions"
          size="sm"
          @change="loadApiKeys"
        />
      </b-form-group>

      <b-form-group class="col-lg-2" label=".">
        <b-button
          size="sm"
          variant="primary"
          block
          @click="loadApiKeys"
        >
          <md-icon icon="search" />
          Search
        </b-button>
      </b-form-group>
    </b-form-row>

    <b-overlay :show="loading">
      <b-table
        :items="apiKeys"
        :fields="tableFields"
        striped
        hover
        small
        responsive
        bordered
        show-empty
        empty-text="No API keys found"
      >
        <template #cell(name)="row">
          <div class="d-flex flex-column">
            <strong>{{ row.item.name }}</strong>
            <small class="text-muted">ID: {{ row.item.id }}</small>
          </div>
        </template>

        <template #cell(environment)="row">
          <ApiKeyBadges type="environment" :value="row.item.environment" />
        </template>

        <template #cell(status)="row">
          <ApiKeyBadges type="status" :value="row.item.status" />
        </template>

        <template #cell(rate_limit)="row">
          <span v-if="row.item.rate_limit">
            {{ row.item.rate_limit }} req/min
          </span>
          <span v-else class="text-muted">Unlimited</span>
        </template>

        <template #cell(ip_whitelist)="row">
          <div v-if="Array.isArray(row.item.ip_whitelist) && row.item.ip_whitelist.length > 0">
            <b-badge
              variant="info"
              v-tippy="{ content: row.item.ip_whitelist.join(', '), arrow: true }"
            >
              {{ row.item.ip_whitelist.length }} IP(s)
            </b-badge>
          </div>
          <span v-else class="text-muted">All IPs</span>
        </template>

        <template #cell(last_used_at)="row">
          <span v-if="row.item.last_used_at" class="text-muted">
            {{ formatRelativeTime(row.item.last_used_at) }}
          </span>
          <span v-else class="text-muted">Never</span>
        </template>

        <template #cell(expires_at)="row">
          <div v-if="row.item.expires_at" class="d-flex align-items-center">
            <md-icon
              :icon="isExpired(row.item.expires_at) ? 'error' : 'schedule'"
              :class="isExpired(row.item.expires_at) ? 'text-danger' : 'text-success'"
              class="mr-1"
            />
            <span :class="isExpired(row.item.expires_at) ? 'text-danger' : ''">
              {{ formatDate(row.item.expires_at) }}
            </span>
          </div>
          <span v-else class="text-muted">No expiration</span>
        </template>

        <template #cell(actions)="row">
          <div class="d-flex justify-content-center">
            <b-button
              size="sm"
              variant="info"
              class="mr-1 px-2"
              @click="viewDetails(row.item)"
              v-tippy="{ content: 'View Details', arrow: true }"
            >
              <md-icon icon="visibility" />
            </b-button>

            <b-button
              size="sm"
              variant="primary"
              class="mr-1 px-2"
              @click="editApiKey(row.item)"
              :disabled="row.item.status === 'revoked'"
              v-tippy="{ content: 'Edit', arrow: true }"
            >
              <md-icon icon="edit" />
            </b-button>

            <b-button
              size="sm"
              variant="warning"
              class="mr-1 px-2"
              @click="regenerateApiKey(row.item)"
              :disabled="row.item.status === 'revoked'"
              v-tippy="{ content: 'Regenerate Key', arrow: true }"
            >
              <md-icon icon="refresh" />
            </b-button>

            <b-button
              size="sm"
              variant="danger"
              class="px-2"
              @click="revokeApiKey(row.item)"
              :disabled="row.item.status === 'revoked'"
              v-tippy="{ content: 'Revoke', arrow: true }"
            >
              <md-icon icon="block" />
            </b-button>
          </div>
        </template>
      </b-table>

      <div v-if="totalRows > limit" class="d-flex justify-content-between align-items-center mt-3">
        <div class="text-muted">
          Showing {{ apiKeys.length }} of {{ totalRows }} keys
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="limit"
          size="sm"
          @change="onPageChange"
        />
      </div>
    </b-overlay>

    <!-- Details Modal -->
    <b-modal
      ref="detailsModal"
      title="API Key Details"
      size="lg"
      hide-footer
    >
      <div v-if="selectedKey" class="api-key-details">
        <b-form-group label="Name">
          <b-form-input :value="selectedKey.name" size="sm" disabled />
        </b-form-group>

        <b-form-row>
          <b-form-group class="col-md-6" label="Environment">
            <div>
              <ApiKeyBadges type="environment" :value="selectedKey.environment" />
            </div>
          </b-form-group>

          <b-form-group class="col-md-6" label="Status">
            <div>
              <ApiKeyBadges type="status" :value="selectedKey.status" />
            </div>
          </b-form-group>
        </b-form-row>

        <b-form-row>
          <b-form-group class="col-md-6" label="Rate Limit">
            <b-form-input
              :value="selectedKey.rate_limit ? `${selectedKey.rate_limit} req/min` : 'Unlimited'"
              size="sm"
              disabled
            />
          </b-form-group>

          <b-form-group class="col-md-6" label="Last Used">
            <b-form-input
              :value="selectedKey.last_used_at ? formatDate(selectedKey.last_used_at) : 'Never'"
              size="sm"
              disabled
            />
          </b-form-group>
        </b-form-row>

        <b-form-group label="API Key">
          <b-input-group size="sm">
            <b-form-input
              :value="getMaskedKey(selectedKey)"
              class="font-monospace"
              disabled
            />
            <template #append>
              <b-button
                v-if="selectedKey.key"
                variant="outline-primary"
                size="sm"
                @click="showKeySecurityModal"
              >
                <md-icon icon="visibility" />
                Show Key
              </b-button>
              <b-button
                v-else
                variant="outline-secondary"
                size="sm"
                disabled
              >
                <md-icon icon="visibility_off" />
                Hidden
              </b-button>
            </template>
          </b-input-group>
          <small v-if="!selectedKey.key" class="text-muted">
            For security, the full key is only shown once when created or regenerated.
          </small>
        </b-form-group>

        <b-form-group label="IP Whitelist">
          <b-form-textarea
            :value="Array.isArray(selectedKey.ip_whitelist) ? selectedKey.ip_whitelist.join('\n') : 'All IPs allowed'"
            rows="3"
            size="sm"
            disabled
          />
        </b-form-group>

        <b-form-row>
          <b-form-group class="col-md-6" label="Created At">
            <b-form-input
              :value="formatDate(selectedKey.created_at)"
              size="sm"
              disabled
            />
          </b-form-group>

          <b-form-group class="col-md-6" label="Expires At">
            <b-form-input
              :value="selectedKey.expires_at ? formatDate(selectedKey.expires_at) : 'No expiration'"
              size="sm"
              disabled
            />
          </b-form-group>
        </b-form-row>

        <div class="text-right mt-3">
          <b-button variant="secondary" @click="$refs.detailsModal.hide()">
            Close
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import ApiKeyBadges from './ApiKeyBadges.vue';
import moment from 'moment';

export default {
  name: 'ApiKeysList',
  components: {
    ApiKeyBadges
  },
  props: {
    supplierId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      loading: false,
      apiKeys: [],
      totalRows: 0,
      currentPage: 1,
      limit: 10,
      offset: 0,
      filters: {
        search: '',
        environment: '',
        status: ''
      },
      selectedKey: null,
      detailsCopyIcon: 'content_copy',
      detailsCopyText: 'Copy',
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'environment', label: 'Environment', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'rate_limit', label: 'Rate Limit' },
        { key: 'ip_whitelist', label: 'IP Whitelist' },
        { key: 'last_used_at', label: 'Last Used', sortable: true },
        { key: 'expires_at', label: 'Expires', sortable: true },
        { key: 'actions', label: 'Actions', class: 'text-center' }
      ],
      environmentFilterOptions: [
        { value: '', text: 'All Environments' },
        { value: 'sandbox', text: 'Sandbox' },
        { value: 'live', text: 'Live' }
      ],
      statusFilterOptions: [
        { value: '', text: 'All Statuses' },
        { value: 'active', text: 'Active' },
        { value: 'inactive', text: 'Inactive' },
        { value: 'revoked', text: 'Revoked' }
      ],
      searchTimeout: null
    };
  },
  mounted() {
    this.loadApiKeys();
  },
  methods: {
    async loadApiKeys() {
      this.loading = true;

      try {
        const params = {
          offset: this.offset,
          limit: this.limit,
          filters: JSON.stringify({
            search: this.filters.search,
            environment: this.filters.environment,
            status: this.filters.status
          })
        };

        const response = await this.$root.api.get(
          `/suppliers/${this.supplierId}/api-keys`,
          { params }
        );

        this.apiKeys = response.data.rows || [];
        this.totalRows = response.data.totalRows || 0;
      } catch (error) {
        this.$root.handleRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.loadApiKeys();
      }, 500);
    },
    onPageChange(page) {
      this.currentPage = page;
      this.offset = (page - 1) * this.limit;
      this.loadApiKeys();
    },
    async viewDetails(apiKey) {
      this.loading = true;
      try {
        const response = await this.$root.api.get(`/suppliers/api-keys/${apiKey.id}/show`);
        this.selectedKey = response.data;
        this.$refs.detailsModal.show();
      } catch (error) {
        this.$root.handleRequestError(error);
      } finally {
        this.loading = false;
      }
    },
    editApiKey(apiKey) {
      this.$emit('edit', apiKey);
    },
    regenerateApiKey(apiKey) {
      this.$root.showWarning({
        title: 'Regenerate API Key?',
        message: 'The current key will be revoked and stop working immediately. A new key will be generated with the same settings.',
        okText: 'Regenerate',
        cancelText: 'Cancel',
        okAction: async (_event, setLoading, close) => {
          setLoading(true);
          try {
            const response = await this.$root.api.post(
              `/suppliers/api-keys/${apiKey.id}/regenerate`
            );

            this.$root.showAlert('API key regenerated successfully', { variant: 'success' });
            this.$emit('regenerated', response.data);
            this.loadApiKeys();
            close();
          } catch (error) {
            if (error.response?.status === 422) {
              this.$root.showAlert(
                error.response.data.message || 'Cannot regenerate this key',
                { variant: 'danger' }
              );
            } else {
              this.$root.handleRequestError(error);
            }
            close();
          }
        }
      });
    },
    revokeApiKey(apiKey) {
      this.$root.showWarning({
        title: 'Revoke API Key?',
        message: 'This action cannot be undone. The key will be permanently revoked and all applications using it will stop working immediately.',
        okText: 'Revoke',
        cancelText: 'Cancel',
        okAction: async (event, setLoading, close) => {
          setLoading(true);
          try {
            await this.$root.api.delete(`/suppliers/api-keys/${apiKey.id}`);

            this.$root.showAlert('API key revoked successfully', { variant: 'warning' });
            this.loadApiKeys();
            close();
          } catch (error) {
            this.$root.handleRequestError(error);
            close();
          }
        }
      });
    },
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY HH:mm');
    },
    formatRelativeTime(date) {
      return moment(date).fromNow();
    },
    isExpired(date) {
      return moment().isAfter(moment(date));
    },
    getMaskedKey(apiKey) {
      if (!apiKey || !apiKey.environment) return '';
      const prefix = apiKey.environment === 'sandbox' ? 'sk_sandbox_' : 'sk_live_';
      return `${prefix}••••••••••••••••••••••••••••••••`;
    },
    copyKeyToClipboard(key) {
      navigator.clipboard.writeText(key)
        .then(() => {
          this.detailsCopyIcon = 'check';
          this.detailsCopyText = 'Copied!';
          setTimeout(() => {
            this.detailsCopyIcon = 'content_copy';
            this.detailsCopyText = 'Copy';
          }, 2000);
        })
        .catch(() => {
          this.$root.showAlert('Failed to copy to clipboard', { variant: 'danger' });
        });
    },
    showKeySecurityModal() {
      if (this.selectedKey && this.selectedKey.key) {
        this.$emit('show-key', this.selectedKey.key);
      }
    }
  },
  beforeDestroy() {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }
};
</script>

<style scoped>
.api-keys-list {
  padding: 1rem 0;
}

.api-key-details .form-control:disabled {
  background-color: #e9ecef;
  opacity: 0.7;
}

.font-monospace {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
}
</style>
